import React, { useCallback } from 'react';
import classes from '~/app/common/scss/checkout.module.scss';
import { useStoreActions, useStoreState } from '~/store';
import Modal from '~/app/common/Modal';
import Icon from '~/app/common/Icon';
import Button from '~/app/common/Button';
import { SavePartResponse } from '~/types';
import useTranslation from '~/app/hooks/useTranslation';
import { PartLinkTypes } from '~/app/data/statuses/issue';
import { formatPrice } from '~/app/helpers/format';
import { getPartStoreKey } from '../../helper';

export interface CheckoutProps {
  open: boolean
  onClose: () => void
  onCloseAll: () => void
  type: PartLinkTypes
}
export type CheckoutComponent = React.FC<CheckoutProps>

const Checkout: CheckoutComponent = ({ open, onClose, onCloseAll, type }) => {

  const basePath = getPartStoreKey(type);
  const id = useStoreState(state => state[basePath].data.id);
  const tempParts = useStoreState(state => state[basePath].tempParts ?? []);
  const savePartAction = useStoreActions(actions => actions[basePath].savePart);
  const fetchPartsAction = useStoreActions(actions => actions[basePath].fetchParts);

  const { t } = useTranslation('parts');

  const visibleParts = tempParts.filter(dM => !dM.deleted);
  const totalPrice = visibleParts.reduce(
    (sum, part) => sum + (Number(part.price_per_unit) || 0) * (Number(part.quantity) || 0),
    0
  );

  const handleSave = useCallback(() => {
    savePartAction({
      id: String(id),
      parts: tempParts
    }).then((res: SavePartResponse) => {
      if (res.success) {
        fetchPartsAction(String(id));
        onCloseAll();
      }
    });
  }, [savePartAction, fetchPartsAction, id, tempParts, onCloseAll]);

  return (
    <Modal className={classes['checkout']} open={open} onClose={onClose} position={'bottom'}>
      <div className={classes['partManager']}>
        <div className={classes['partsHeader']}>
          <span>{t('reviewParts')}</span>
          <Icon name="close" onClick={onClose} />
        </div>
        <div className={classes['partsList']}>
          {visibleParts.map((part) => {
            const lineTotal = (Number(part.price_per_unit) || 0) * (Number(part.quantity) || 0);
            return (
              <div className={classes['partItem']} key={part.id}>
                <div className={classes['column']}>
                  <span className={classes['partName']}>{part.article_name}</span>
                  <span className={classes['partNumber']}>
                    {t('partNo')}: {part.article_no} · {part.quantity} {t('item')}
                  </span>
                </div>
                <span className={classes['linePrice']}>{formatPrice(lineTotal)}</span>
              </div>
            );
          })}
        </div>
        <div className={classes['totalRow']}>
          <span>{t('total')}</span>
          <span className={classes['totalPrice']}>{formatPrice(totalPrice)}</span>
        </div>
        <Button className={classes['saveButton']} onClick={handleSave}>{t('saveParts')}</Button>
      </div>
    </Modal>
  );
};

export default Checkout;
